import _toConsumableArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import React from "react";
var __jsx = React.createElement;
import { useMemo } from "react";
import Markdown from "markdown-to-jsx";
import { PURCHASE_METHOD_CASH, PURCHASE_METHOD_NORMAL_FINANCE, PURCHASE_METHOD_TAMRA, PURCHASE_METHOD_TABBY, PURCHASE_METHOD_AMWAL, PURCHASE_METHOD_ARIB, PURCHASE_METHOD_CREDIT_CARD, useCheckoutOrder } from "@/common/Contexts/CheckoutOrderContext";
import RadioCardNew from "@/pages/checkout/pre-checkout/RadioCard/RadioCardNew";
import PreCheckoutOptionsShimmer from "@/pages/checkout/pre-checkout/PreCheckoutOptionsShimmer";
import FinanceModal from "@/pages/checkout/pre-checkout/PurchaseMethods/FinanceModal";
import TabbyModal from "@/pages/checkout/pre-checkout/PurchaseMethods/TabbyModal";
import TamaraModal from "@/pages/checkout/pre-checkout/PurchaseMethods/TamaraModal";
import AmwalModal from "@/pages/checkout/pre-checkout/PurchaseMethods/AmwalModal";
import useBoolean from "@hooks/useBoolean";
import RiyalSymbol from "@componentsShared/InputCurrency/RiyalSymbol";

// ─── Helpers ──────────────────────────────────────────────────────────────────

var BNPL_MAP = {
  tamara: PURCHASE_METHOD_TAMRA,
  tabby: PURCHASE_METHOD_TABBY,
  amwal: PURCHASE_METHOD_AMWAL,
  arib: PURCHASE_METHOD_ARIB
};
var getPurchaseMethod = function getPurchaseMethod(m) {
  var _BNPL_MAP, _m$finance_type_en;
  if (m.type === 'cash') return PURCHASE_METHOD_CASH;
  if (m.type === 'free_finance') {
    return m.finance_type_en === 'arib' ? PURCHASE_METHOD_ARIB : PURCHASE_METHOD_NORMAL_FINANCE;
  }
  if (m.type === 'arib_finance') return PURCHASE_METHOD_ARIB;
  if (m.type === 'dynamic_finance') return (_BNPL_MAP = BNPL_MAP[(_m$finance_type_en = m.finance_type_en) !== null && _m$finance_type_en !== void 0 ? _m$finance_type_en : '']) !== null && _BNPL_MAP !== void 0 ? _BNPL_MAP : null;
  if (m.type === 'credit_card') return PURCHASE_METHOD_CREDIT_CARD;
  return null;
};

// ─── Types ────────────────────────────────────────────────────────────────────

// ─── Component ────────────────────────────────────────────────────────────────

var PreCheckoutPurchaseMethodsV2 = function PreCheckoutPurchaseMethodsV2(_ref) {
  var _salePrice$replace;
  var methods = _ref.methods,
    loading = _ref.loading,
    salePrice = _ref.salePrice,
    financeSteps = _ref.financeSteps,
    postIsNew = _ref.postIsNew,
    postId = _ref.postId,
    g4Data = _ref.g4Data,
    cashStory = _ref.cashStory,
    tabbyEligible = _ref.tabbyEligible,
    tabbyRejectionMessage = _ref.tabbyRejectionMessage,
    tamaraEligible = _ref.tamaraEligible,
    tamaraRejectionMessage = _ref.tamaraRejectionMessage,
    isLoggedIn = _ref.isLoggedIn;
  var _useCheckoutOrder$sta = useCheckoutOrder().state,
    purchaseMethod = _useCheckoutOrder$sta.purchaseMethod,
    financeModal = _useCheckoutOrder$sta.financeModal;
  var action__setPurchaseMethod = useCheckoutOrder().actions.action__setPurchaseMethod;
  var tabbyModal = useBoolean();
  var tamaraModal = useBoolean();
  var amwalModal = useBoolean();
  var numericPrice = Number((_salePrice$replace = salePrice === null || salePrice === void 0 ? void 0 : salePrice.replace(",", "")) !== null && _salePrice$replace !== void 0 ? _salePrice$replace : 0);

  // Sort by `order` field (stable — items without `order` keep their original position)
  var sorted = useMemo(function () {
    if (loading || !(methods !== null && methods !== void 0 && methods.length)) return [];
    return _toConsumableArray(methods).sort(function (a, b) {
      var _a$order, _b$order;
      return ((_a$order = a.order) !== null && _a$order !== void 0 ? _a$order : 0) - ((_b$order = b.order) !== null && _b$order !== void 0 ? _b$order : 0);
    });
  }, [methods, loading]);

  // Sort: move ineligible BNPL options (tabby / tamara) to the bottom
  var sortedWithTabby = useMemo(function () {
    var options = _toConsumableArray(sorted);
    var isIneligible = function isIneligible(m) {
      return m.type === 'dynamic_finance' && (m.finance_type_en === 'tabby' && tabbyEligible === false || m.finance_type_en === 'tamara' && tamaraEligible === false);
    };
    options.sort(function (a, b) {
      if (isIneligible(a) && !isIneligible(b)) return 1;
      if (isIneligible(b) && !isIneligible(a)) return -1;
      return 0;
    });
    return options;
  }, [sorted, tabbyEligible, tamaraEligible]);
  if (loading || !(methods !== null && methods !== void 0 && methods.length)) return /*#__PURE__*/React.createElement(PreCheckoutOptionsShimmer, null);

  // Split into groups:
  // 1. preItems   — payment methods before the BNPL section title (e.g. cash)
  // 2. bnplHeader — the section title item (id === "payment_method_title")
  // 3. bnplItems  — dynamic_finance items (tamara, tabby, amwal…)
  // 4. postItems  — payment methods after BNPL (e.g. disabled finance at order=1000)
  var bnplTitleIndex = sorted.findIndex(function (m) {
    return m.id === 'payment_method_title';
  });
  var bnplHeader = bnplTitleIndex >= 0 ? sorted[bnplTitleIndex] : null;
  var preItems = sorted.filter(function (m, i) {
    return m.id !== 'purchase_method_header' && m.id !== 'payment_method_title' && m.type !== 'dynamic_finance' && (bnplTitleIndex === -1 || i < bnplTitleIndex) && getPurchaseMethod(m) !== null;
  });
  var bnplItems = sorted.filter(function (m) {
    return m.type === 'dynamic_finance';
  });
  var postItems = sorted.filter(function (m, i) {
    return m.id !== 'purchase_method_header' && m.id !== 'payment_method_title' && m.type !== 'dynamic_finance' && bnplTitleIndex >= 0 && i > bnplTitleIndex && getPurchaseMethod(m) !== null;
  });
  var renderCard = function renderCard(method, index) {
    var _method$storyly, _method$storyly$actio, _method$storyly2, _method$finance_type_, _method$title, _method$title2, _method$extra_sub_tit, _method$extra_sub_tit2, _method$finance_type_2, _method$disabled_mess;
    var purchaseMethodValue = getPurchaseMethod(method);
    if (!purchaseMethodValue) return null;
    var isBnpl = method.type === 'dynamic_finance';
    var isCreditCard = method.type === 'credit_card';
    var isTabby = isBnpl && method.finance_type_en === 'tabby';
    var isTabbyDisabled = isTabby && tabbyEligible === false;
    var isTamara = isBnpl && method.finance_type_en === 'tamara';
    var isTamaraDisabled = isTamara && tamaraEligible === false;
    var isCashStory = ((_method$storyly = method.storyly) === null || _method$storyly === void 0 ? void 0 : (_method$storyly$actio = _method$storyly.action) === null || _method$storyly$actio === void 0 ? void 0 : _method$storyly$actio.type) === 'cashStoryly';
    var hasCta = !!((_method$storyly2 = method.storyly) !== null && _method$storyly2 !== void 0 && _method$storyly2.title);
    var handleCta = function handleCta(e) {
      var _method$storyly3, _method$storyly3$acti;
      e.preventDefault();
      e.stopPropagation();
      var actionType = (_method$storyly3 = method.storyly) === null || _method$storyly3 === void 0 ? void 0 : (_method$storyly3$acti = _method$storyly3.action) === null || _method$storyly3$acti === void 0 ? void 0 : _method$storyly3$acti.type;
      if (actionType === 'financeStoryly') return financeModal.setTrue();
      if (actionType === 'dynamicFinance') {
        if (method.finance_type_en === 'tamara') return tamaraModal.setTrue();
        if (method.finance_type_en === 'tabby') return tabbyModal.setTrue();
        if (method.finance_type_en === 'amwal') return amwalModal.setTrue();
        if (method.type === 'credit_card') return amwalModal.setTrue();
      }
    };
    var cardTitle = isBnpl ? /*#__PURE__*/React.createElement("div", {
      className: "bg-[#F9F9FD] flex items-center p-1 rounded border border-solid border-[#e9e9e9] gap-3 w-full"
    }, method.title_icon && /*#__PURE__*/React.createElement("img", {
      src: method.title_icon,
      alt: (_method$finance_type_ = method.finance_type_en) !== null && _method$finance_type_ !== void 0 ? _method$finance_type_ : '',
      width: "65",
      height: "25",
      className: "object-contain"
    }), /*#__PURE__*/React.createElement("span", {
      className: "text-black-primary text-xs font-medium"
    }, /*#__PURE__*/React.createElement(Markdown, null, (_method$title = method.title) !== null && _method$title !== void 0 ? _method$title : ''))) : /*#__PURE__*/React.createElement(Markdown, null, (_method$title2 = method.title) !== null && _method$title2 !== void 0 ? _method$title2 : '');
    var hasSubTitle = !!method.sub_title;
    var hasIcons = ((_method$extra_sub_tit = (_method$extra_sub_tit2 = method.extra_sub_title_icons) === null || _method$extra_sub_tit2 === void 0 ? void 0 : _method$extra_sub_tit2.length) !== null && _method$extra_sub_tit !== void 0 ? _method$extra_sub_tit : 0) > 0;
    var cardDescription = isBnpl ? !hasSubTitle && hasIcons ? /*#__PURE__*/React.createElement("span", {
      className: "flex items-center justify-between w-full"
    }, hasCta && /*#__PURE__*/React.createElement("span", {
      className: "text-primary-new text-xs font-semibold underline"
    }, method.storyly.title), /*#__PURE__*/React.createElement("span", {
      className: "flex gap-1.5 items-center"
    }, method.extra_sub_title_icons.map(function (icon, i) {
      return /*#__PURE__*/React.createElement("img", {
        key: i,
        src: icon,
        alt: "",
        height: 19,
        width: 19,
        className: "w-[40px] h-[30px] object-contain md:h-[30px] md:w-[40px]"
      });
    }))) : /*#__PURE__*/React.createElement("span", {
      className: "flex gap-3 w-full justify-between"
    }, /*#__PURE__*/React.createElement("span", {
      className: "max-w-[370px] md:flex md:flex-col"
    }, method.sub_title && /*#__PURE__*/React.createElement("span", null, method.sub_title)), hasIcons && /*#__PURE__*/React.createElement("span", {
      className: "flex shrink-0 gap-1.5 items-end"
    }, method.extra_sub_title_icons.map(function (icon, i) {
      return /*#__PURE__*/React.createElement("img", {
        key: i,
        src: icon,
        alt: "",
        height: 19,
        width: 19,
        className: "w-[40px] h-[30px] object-contain md:h-[30px] md:w-[40px]"
      });
    }))) : method.type === 'credit_card' && hasIcons ? /*#__PURE__*/React.createElement("span", {
      className: "flex flex-col gap-1 w-full"
    }, method.sub_title && /*#__PURE__*/React.createElement("span", {
      className: "max-w-[370px]"
    }, method.sub_title), /*#__PURE__*/React.createElement("span", {
      className: "flex items-center justify-between w-full"
    }, hasCta && /*#__PURE__*/React.createElement("span", {
      className: "text-primary-new text-xs font-semibold underline"
    }, method.storyly.title), /*#__PURE__*/React.createElement("span", {
      className: "flex shrink-0 gap-1.5 items-center"
    }, method.extra_sub_title_icons.map(function (icon, i) {
      return /*#__PURE__*/React.createElement("img", {
        key: i,
        src: icon,
        alt: "",
        height: 19,
        width: 19,
        className: "w-[40px] h-[30px] object-contain md:h-[30px] md:w-[40px]"
      });
    })))) : method.sub_title;
    var priceBox = method.trailing_content ? /*#__PURE__*/React.createElement("div", {
      className: "flex gap-1.5 items-center py-px px-1.5 rounded-md bg-[#E6F7EF]"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex items-center gap-1"
    }, /*#__PURE__*/React.createElement("p", {
      className: "text-[#00B362] font-bold text-base leading-6"
    }, method.trailing_content.title), /*#__PURE__*/React.createElement(RiyalSymbol, {
      className: "text-[#00B362] text-base font-bold leading-6"
    })), /*#__PURE__*/React.createElement("p", {
      className: "text-start text-[#00143C] text-xs font-semibold leading-tight"
    }, method.trailing_content.sub_title)) : undefined;
    var storyContent = isCashStory && cashStory ? cashStory.data.map(function (story, idx) {
      return /*#__PURE__*/React.createElement("div", {
        key: idx,
        className: "singleStry",
        "data-color": "linear-gradient(180deg, rgba(48,75,165,1) 0%, rgba(74,167,221,1) 100%);"
      }, /*#__PURE__*/React.createElement("strong", {
        style: {
          textAlign: "center"
        }
      }, cashStory.title), /*#__PURE__*/React.createElement("img", {
        src: story.image,
        alt: "story"
      }));
    }) : undefined;
    return /*#__PURE__*/React.createElement(RadioCardNew, {
      key: "".concat(method.id, "-").concat(index),
      id: "precheckout-".concat(method.type, "-").concat((_method$finance_type_2 = method.finance_type_en) !== null && _method$finance_type_2 !== void 0 ? _method$finance_type_2 : 'option'),
      name: "purchase_method",
      value: purchaseMethodValue,
      checked: purchaseMethod === purchaseMethodValue,
      disabled: method.enabled === false || isTabbyDisabled || isTamaraDisabled,
      disabledNote: isTabbyDisabled ? tabbyRejectionMessage !== null && tabbyRejectionMessage !== void 0 ? tabbyRejectionMessage : undefined : isTamaraDisabled ? tamaraRejectionMessage !== null && tamaraRejectionMessage !== void 0 ? tamaraRejectionMessage : undefined : (_method$disabled_mess = method.disabled_message) !== null && _method$disabled_mess !== void 0 ? _method$disabled_mess : undefined,
      onChange: function onChange(e) {
        action__setPurchaseMethod(e.target.value);
      },
      title: cardTitle,
      description: cardDescription,
      ctaText: hasCta && (hasSubTitle || !hasIcons) && !(isCreditCard && hasIcons) ? method.storyly.title : undefined,
      onClickCta: hasCta ? handleCta : undefined,
      story: storyContent,
      showStoryModal: isCashStory,
      priceBox: priceBox
    });
  };
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4 md:pb-4"
  }, preItems.map(function (m, i) {
    return renderCard(m, i);
  })), bnplItems.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "mx-[-16px] bg-white px-4 py-4 md:mx-[-42px] md:px-[42px] md:pb-6"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4"
  }, bnplHeader && /*#__PURE__*/React.createElement("h3", {
    className: "text-[#00143C] font-medium md:text-xl"
  }, bnplHeader.title), sortedWithTabby.filter(function (m) {
    return m.type === 'dynamic_finance';
  }).map(function (m, i) {
    return renderCard(m, i);
  }))), postItems.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4 md:pb-4"
  }, postItems.map(function (m, i) {
    return renderCard(m, i);
  })), /*#__PURE__*/React.createElement(FinanceModal, {
    isOpen: financeModal.value,
    onClose: financeModal.setFalse,
    isNew: !!postIsNew,
    postId: postId,
    financeSteps: financeSteps,
    g4Data: g4Data
  }), /*#__PURE__*/React.createElement(TabbyModal, {
    isOpen: tabbyModal.value,
    onClose: tabbyModal.setFalse,
    price: numericPrice
  }), /*#__PURE__*/React.createElement(TamaraModal, {
    isOpen: tamaraModal.value,
    onClose: tamaraModal.setFalse,
    amount: numericPrice
  }), /*#__PURE__*/React.createElement(AmwalModal, {
    isOpen: amwalModal.value,
    onClose: amwalModal.setFalse,
    price: numericPrice
  }));
};
export default PreCheckoutPurchaseMethodsV2;